/**
 * @module-isolated analytics-cohort
 * Universo = leads únicos con Creado at Kommo en el período.
 * Resultado = estado actual HOY (espejo). Sin historial ni dwell.
 */
import { emptyMeetingCounts } from '../../../shared/constants/meeting-statuses.js';

export const ORIGIN_META = '__meta__';
export const ORIGIN_NONE = '__none__';
export const ORIGIN_UNCLASSIFIED = '__unclassified__';
export const CAMPAIGN_NONE = '__no_campaign__';
export const STATE_UNCLASSIFIED = '__unclassified_state__';
export const KOMMO_STAGE_EMPTY = '__no_kommo_stage__';

export function isMetaLead(lead) {
  return !!(lead && (lead.originIsMeta === true || lead.originStatus === 'meta'));
}

/**
 * Agrupa variantes de etiqueta Kommo de la misma campaña Meta
 * (p. ej. «Campaña X», «Campaña X - Landing»).
 */
export function normalizeCampaignSlug(raw) {
  return String(raw || '')
    .normalize('NFKC')
    .trim()
    .replace(/^campaña\s+/i, '')
    .replace(/\s*-\s*landing\s*$/i, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function preferCampaignLabel_(next, prev) {
  const n = String(next || '').trim();
  const p = String(prev || '').trim();
  if (!p) return true;
  if (!n) return false;
  const nLanding = /\s-\s*landing$/i.test(n);
  const pLanding = /\s-\s*landing$/i.test(p);
  if (pLanding && !nLanding) return true;
  if (!pLanding && nLanding) return false;
  return n.length < p.length;
}

function campaignTagsOf(lead) {
  const chunks = [];
  const origin = String((lead && lead.originTag) || '').trim();
  if (origin) chunks.push(origin);
  String((lead && lead.tagsText) || '')
    .split(/[;|,]/)
    .forEach((part) => chunks.push(part));
  const out = [];
  const seen = new Set();
  chunks.forEach((part) => {
    const raw = String(part || '').trim();
    const slug = normalizeCampaignSlug(raw);
    if (!slug || slug === 'meta' || seen.has(slug)) return;
    if (/landing|patagon|contacto web|gicapital|gi capital/.test(slug)) return;
    if (!/campa|invierte|lead ads|leadads/.test(slug)) return;
    seen.add(slug);
    out.push({
      slug: slug,
      label: raw.replace(/\s*-\s*Landing\s*$/i, '').trim() || raw
    });
  });
  return out;
}

/**
 * Elige el dato que realmente separa campañas: id de Ads, nombre de Ads o etiqueta.
 * Un nombre compartido no puede tapar varias campañas.
 */
export function detectCampaignMode(leads) {
  const ids = new Set();
  const names = new Set();
  const tagFreq = new Map();
  uniqueByLeadId(leads).forEach((lead) => {
    if (!isMetaLead(lead)) return;
    const id = String(lead.metaCampaignId || '').trim();
    const name = normalizeCampaignSlug(lead.metaCampaignName);
    if (id) ids.add(id);
    if (name) names.add(name);
    campaignTagsOf(lead).forEach((tag) => {
      tagFreq.set(tag.slug, (tagFreq.get(tag.slug) || 0) + 1);
    });
  });
  const options = [
    { mode: 'id', n: ids.size },
    { mode: 'name', n: names.size },
    { mode: 'tag', n: tagFreq.size }
  ];
  options.sort((a, b) => b.n - a.n);
  return { mode: options[0].n > 0 ? options[0].mode : 'name', tagFreq: tagFreq };
}

/** Etiqueta visible según el corte elegido. */
export function campaignLabelOf(lead, grouping) {
  const mode = grouping && grouping.mode;
  const id = String((lead && lead.metaCampaignId) || '').trim();
  const name = String((lead && lead.metaCampaignName) || '').trim();
  if (mode === 'tag') {
    const tags = campaignTagsOf(lead).slice().sort((a, b) => {
      const fa = grouping.tagFreq ? grouping.tagFreq.get(a.slug) || 0 : 0;
      const fb = grouping.tagFreq ? grouping.tagFreq.get(b.slug) || 0 : 0;
      return fa - fb;
    });
    if (tags.length) return tags[0].label;
  }
  if (name && mode !== 'tag') return name;
  if (name) return name;
  const tag = String((lead && lead.originTag) || '').trim();
  if (tag && tag.toLowerCase() !== 'meta') {
    return tag.replace(/\s*-\s*Landing\s*$/i, '').trim() || tag;
  }
  return id || 'Sin campaña Meta';
}

/**
 * Clave de color/filtro. «Campaña X» y «Campaña X - Landing» siguen juntas.
 * Si hay varios id o varias etiquetas, no se aplasta todo en un solo nombre.
 */
export function campaignKeyOf(lead, grouping) {
  const mode = grouping && grouping.mode;
  const id = String((lead && (lead.metaCampaignId || lead.campaignKey)) || '').trim();
  const name = normalizeCampaignSlug(lead && lead.metaCampaignName);
  const tags = campaignTagsOf(lead).slice().sort((a, b) => {
    const fa = grouping && grouping.tagFreq ? grouping.tagFreq.get(a.slug) || 0 : 0;
    const fb = grouping && grouping.tagFreq ? grouping.tagFreq.get(b.slug) || 0 : 0;
    return fa - fb;
  });
  const tag = tags.length ? tags[0].slug : '';
  if (mode === 'id' && id) return 'id:' + id;
  if (mode === 'tag' && tag) return 'tag:' + tag;
  if (mode === 'name' && name) return 'name:' + name;
  if (id) return 'id:' + id;
  if (name) return 'name:' + name;
  if (tag) return 'tag:' + tag;
  return '';
}

export function uniqueByLeadId(leads) {
  const seen = new Set();
  const out = [];
  (leads || []).forEach((lead) => {
    const id = String((lead && lead.leadId) || '');
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(lead);
  });
  return out;
}

export function listCampaigns(leads) {
  const map = new Map();
  const grouping = detectCampaignMode(leads);
  uniqueByLeadId(leads).forEach((lead) => {
    if (!isMetaLead(lead)) return;
    const key = campaignKeyOf(lead, grouping) || CAMPAIGN_NONE;
    const label = key === CAMPAIGN_NONE ? 'Sin campaña Meta' : campaignLabelOf(lead, grouping);
    const prev = map.get(key);
    if (!prev) map.set(key, { id: key, label: label, count: 1 });
    else {
      prev.count += 1;
      if (preferCampaignLabel_(label, prev.label)) prev.label = label;
    }
  });
  return Array.from(map.values()).sort((a, b) => {
    if (a.id === CAMPAIGN_NONE) return 1;
    if (b.id === CAMPAIGN_NONE) return -1;
    return String(a.label).localeCompare(String(b.label), 'es');
  });
}

/**
 * @param {Array} leads
 * @param {{ originIds: string[], campaignIds: string[], allMetaCampaigns: boolean }} sel
 */
export function filterCohort(leads, sel) {
  const origins = new Set(((sel && sel.originIds) || []).map(String));
  const camps = new Set(((sel && sel.campaignIds) || []).map(String));
  const metaOn = origins.has(ORIGIN_META);
  const allMeta = sel && sel.allMetaCampaigns === true;
  const grouping = detectCampaignMode(leads);
  const seen = new Set();
  const out = [];
  (leads || []).forEach((lead) => {
    const id = String((lead && lead.leadId) || '');
    if (!id || seen.has(id)) return;
    let ok = false;
    if (isMetaLead(lead)) {
      if (metaOn) {
        if (allMeta) ok = true;
        else {
          const ck = campaignKeyOf(lead, grouping) || CAMPAIGN_NONE;
          const rawId = String(lead.metaCampaignId || '').trim();
          ok = camps.has(ck) || (rawId && camps.has(rawId));
        }
      }
    } else if (lead.originStatus === 'none' && origins.has(ORIGIN_NONE)) {
      ok = true;
    } else if (lead.originStatus === 'unclassified' && origins.has(ORIGIN_UNCLASSIFIED)) {
      ok = true;
    } else if (lead.originTag && origins.has(String(lead.originTag))) {
      ok = true;
    }
    if (!ok) return;
    seen.add(id);
    out.push(lead);
  });
  return out;
}

export function distributeByState(leads, internalStates) {
  const catalog = Array.isArray(internalStates) ? internalStates : [];
  const byId = {};
  catalog.forEach((s) => {
    const id = String((s && s.stateId) || '');
    if (!id) return;
    byId[id] = {
      id: id,
      name: String(s.name || id),
      order: s.order == null || Number.isNaN(Number(s.order)) ? 999 : Number(s.order),
      count: 0
    };
  });
  let unclassified = 0;
  uniqueByLeadId(leads).forEach((lead) => {
    if (lead.unclassified || !lead.internalStateId) {
      unclassified += 1;
      return;
    }
    const sid = String(lead.internalStateId);
    if (!byId[sid]) {
      byId[sid] = {
        id: sid,
        name: String(lead.internalStateName || sid),
        order: 800,
        count: 0
      };
    }
    byId[sid].count += 1;
  });
  const rows = Object.keys(byId)
    .map((k) => byId[k])
    .filter((r) => r.count > 0)
    .sort(
      (a, b) => a.order - b.order || String(a.name).localeCompare(String(b.name), 'es')
    );
  if (unclassified > 0) {
    rows.push({
      id: STATE_UNCLASSIFIED,
      name: 'Sin clasificar',
      order: 9999,
      count: unclassified
    });
  }
  const total = uniqueByLeadId(leads).length;
  const sum = rows.reduce((n, r) => n + r.count, 0);
  return { rows: rows, total: total, sum: sum, reconciled: sum === total };
}

export function leadsInState(leads, stateId) {
  const list = uniqueByLeadId(leads);
  if (!stateId) return list;
  if (stateId === STATE_UNCLASSIFIED) {
    return list.filter((l) => l.unclassified || !l.internalStateId);
  }
  return list.filter((l) => String(l.internalStateId) === String(stateId) && !l.unclassified);
}

export function kommoStageKey(lead) {
  const pid = String((lead && lead.pipelineId) || '').trim();
  const sid = String((lead && lead.stageId) || '').trim();
  if (pid && sid) return pid + '::' + sid;
  const pn = String((lead && lead.pipelineName) || '').trim();
  const sn = String((lead && lead.stageName) || '').trim();
  if (pn || sn) return 'name::' + pn + '::' + sn;
  return KOMMO_STAGE_EMPTY;
}

/** Distribución por etapa real de Kommo (sin mapeo GI). */
export function distributeByKommoStage(leads) {
  const byId = {};
  uniqueByLeadId(leads).forEach((lead) => {
    const id = kommoStageKey(lead);
    const stageName = String((lead && (lead.stageName || lead.stageId)) || '').trim();
    const pipeName = String((lead && (lead.pipelineName || lead.pipelineId)) || '').trim();
    let name = 'Sin etapa Kommo';
    if (id !== KOMMO_STAGE_EMPTY) {
      name = stageName || id;
      if (pipeName && stageName && pipeName !== stageName) name = stageName + ' · ' + pipeName;
    }
    if (!byId[id]) byId[id] = { id: id, name: name, count: 0 };
    byId[id].count += 1;
  });
  const rows = Object.keys(byId)
    .map((k) => byId[k])
    .sort(
      (a, b) => b.count - a.count || String(a.name).localeCompare(String(b.name), 'es')
    );
  const total = uniqueByLeadId(leads).length;
  const sum = rows.reduce((n, r) => n + r.count, 0);
  return { rows: rows, total: total, sum: sum, reconciled: sum === total };
}

export function leadsInKommoStage(leads, stageKey) {
  const list = uniqueByLeadId(leads);
  if (!stageKey) return list;
  return list.filter((l) => kommoStageKey(l) === String(stageKey));
}

/** Filas para SimplePieChart: mayores primero; el resto en Otros. */
export function distToPieRows(rows, maxSlices) {
  const cap = maxSlices == null ? 8 : Number(maxSlices);
  const list = (rows || [])
    .filter((r) => r && Number(r.count) > 0)
    .slice()
    .sort(
      (a, b) =>
        Number(b.count) - Number(a.count) ||
        String(a.name).localeCompare(String(b.name), 'es')
    );
  if (list.length <= cap) {
    return list.map((r) => ({ label: String(r.name || r.id), value: Number(r.count) }));
  }
  const head = list.slice(0, cap - 1);
  const other = list.slice(cap - 1).reduce((n, r) => n + Number(r.count), 0);
  const out = head.map((r) => ({ label: String(r.name || r.id), value: Number(r.count) }));
  if (other > 0) out.push({ label: 'Otros', value: other });
  return out;
}

/** meetingStatus = etapa Kommo HOY mapeada en Sales_Meeting_Stages. */
export function countByMeetingStatus(leads) {
  const counts = emptyMeetingCounts();
  uniqueByLeadId(leads).forEach((l) => {
    const s = String((l && l.meetingStatus) || '');
    if (counts[s] != null) counts[s] += 1;
  });
  return counts;
}

export function filterByMeeting(leads, meetingFilter) {
  const list = uniqueByLeadId(leads);
  if (!meetingFilter) return list;
  if (meetingFilter === 'yes') return list.filter((l) => l.hadMeeting === true);
  if (meetingFilter === 'no') return list.filter((l) => l.hadMeeting !== true);
  return list.filter((l) => String(l.meetingStatus || '') === String(meetingFilter));
}

export function originHint(sel, campaignCount) {
  const origins = (sel && sel.originIds) || [];
  const metaOn = origins.indexOf(ORIGIN_META) >= 0;
  const others = origins.filter((id) => id !== ORIGIN_META);
  const parts = [];
  if (metaOn) {
    if (sel && sel.allMetaCampaigns) parts.push('Meta · todas las campañas');
    else {
      const n = ((sel && sel.campaignIds) || []).length;
      parts.push(n === 1 ? 'Meta · 1 campaña' : 'Meta · ' + n + ' campañas');
    }
  }
  if (others.length === 1) parts.push(others[0] === ORIGIN_NONE ? 'Sin etiqueta' : others[0] === ORIGIN_UNCLASSIFIED ? 'Sin clasificar' : others[0]);
  else if (others.length > 1) parts.push(others.length + ' orígenes');
  if (!parts.length) return campaignCount != null ? '' : 'Ningún origen';
  return parts.join(' · ');
}
